import Accessor from '@arcgis/core/core/Accessor';

import { property, subclass } from '@arcgis/core/core/accessorSupport/decorators';

import { whenDefinedOnce } from '@arcgis/core/core/watchUtils';
import Onboarding from '../../widgets/Onboarding';

import appConfigJSON from "../../../config/appConfig.json";
import AppTypeTile from '../../widgets/AppTypeTile';
import Collection from '@arcgis/core/core/Collection';
import SearchBox from '../../widgets/SearchBox';
import AppTile from '../../widgets/AppTile';
import { isNoResourceInQueryString } from '../../utils/appUtils';

export type sortFields =
  "created" | "appTypeTitle" | "compatibleMsgs" | "badge" | // "Browse All" and "Featured"
  "modified" | "title"; // "My Apps"
export type navbarRoute = "browseAll" | "featured" | "myApps";

const DEFAULT_SORT_FIELD: sortFields = isNoResourceInQueryString() ? "appTypeTitle" : "compatibleMsgs"; // "Browse All" and "Featured"
const DEFAULT_SORT_MYAPPS: sortFields = "modified"; // "My Apps"

@subclass('Landing.ui.NavBar.NavBarViewModel')
export default class NavBarViewModel extends Accessor {

  @property()
  navigationRoute: navbarRoute;

  @property()
  sortField: sortFields;

  @property()
  selectedSortField: string;

  @property()
  onboardingModal: Onboarding;

  @property({ readOnly: true })
  selectedFilterTag: string;

  @property()
  searchBox: SearchBox;

  @property()
  appMessages;

  // State variables for Search Box filters
  private _browseFeatFilterState: string;
  private _myAppsFilterState: string;

  // State variables for sort selection
  private _browseFeatSortState: sortFields; // Browse All and Featured Tabs sort state
  private _myAppsSortState: sortFields;

  constructor(params?: any) {
    super(params);
  }

  openOnboarding(): void {
    if (this.onboardingModal == null) {
      this.onboardingModal = new Onboarding({
        releaseTag: appConfigJSON.releaseTag
      });
    }
    this.onboardingModal.open();
  }

  sortTiles(sortField: sortFields, tiles: AppTypeTile[] | AppTile[]) {

    /** Resolve value based on tile type */
    function _decideVal(tile: AppTypeTile | AppTile, field: string): any {
      if (tile.identifier === "AppTile") {
        let value = tile.appInfo[field];
        if (typeof value === "string") {
          value = value.toLocaleLowerCase(); // for title field, sort needs as all lowercase
        }
        return value;
      } else if (tile.identifier === "AppTypeTile") {
        return tile.appTypeInfo[field];
      }
    }

    if (sortField === "created" || sortField === "modified" || sortField === "title" || sortField === "appTypeTitle") {
      tiles.sort((a, b) => {
        const aField = _decideVal(a, sortField);
        const bField = _decideVal(b, sortField);
        if (aField < bField) { return -1; }
        else if (aField > bField) { return 1; }
        else { return 0; }
      });
      if (sortField === "created" || sortField === "modified") { tiles.reverse(); } // sort above puts dates from lowest to highest, so just reverse to get most recent dates first
    } else if (sortField === "badge") {
      tiles.sort((a, b) => {
        const aField = a.appTypeInfo[sortField] ?? "zzzzzzzzzzzzzzzzzzzzzzzz";
        const bField = b.appTypeInfo[sortField] ?? "zzzzzzzzzzzzzzzzzzzzzzzz";
        if (aField < bField) { return -1; }
        else if (aField > bField) { return 1; }
        else { return 0; }
      });
    } else if (sortField === "compatibleMsgs") {
      tiles.sort((a, b) => {
        const aCompatMsgExists: boolean = a.appTypeInfo[sortField]?.length > 0;
        const bCompatMsgExists: boolean = b.appTypeInfo[sortField]?.length > 0;
        if (aCompatMsgExists === bCompatMsgExists) { // both have compat msgs or both don't, then sort alphabetically
          const aUrlField = a.appTypeInfo.appTypeTitle;
          const bUrlField = b.appTypeInfo.appTypeTitle;
          if (aUrlField < bUrlField) { return -1; }
          else if (aUrlField > bUrlField) { return 1; }
          else { return 0; }
        } else if (!aCompatMsgExists) {
          return -1;
        } else if (!bCompatMsgExists) {
          return 1;
        } else {
          return 0;
        }
      });
    }
  }

  setFilterTag(tagStr: string) {
    switch (this.navigationRoute) {
      case "browseAll":
        this._browseFeatFilterState = tagStr;
        break;
      case "featured":
        this._browseFeatFilterState = tagStr;
        break;
      case "myApps":
        this._myAppsFilterState = tagStr;
        break;
    }
    this._set("selectedFilterTag", tagStr);
  }

  setCurrentSearchFilter() {
    const { search_appTypes, search_apps } = this.appMessages.searchBox;
    const { navigationRoute, _browseFeatFilterState, _myAppsFilterState } = this;
    switch (navigationRoute) {
      case "browseAll":
        this.searchBox.setSearchBoxString(_browseFeatFilterState != null ? _browseFeatFilterState : "", search_appTypes, true);
        break;
      case "featured":
        this.searchBox.setSearchBoxString(_browseFeatFilterState != null ? _browseFeatFilterState : "", search_appTypes, true);
        break;
      case "myApps":
        this.searchBox.setSearchBoxString(_myAppsFilterState != null ? _myAppsFilterState : "", search_apps, false);
        break;
    }
  }

  /** Sets the sort for the current tab. If there is a saved sort, sets that, otherwise uses default sort for tab */
  setCurrentSort(_sortTermsToSortFields: [string, sortFields][]): void {
    let sortField: sortFields;
    switch (this.navigationRoute) {
      case "browseAll":
        sortField = this._browseFeatSortState != null ? this._browseFeatSortState : DEFAULT_SORT_FIELD;
        break;
      case "featured":
        sortField = this._browseFeatSortState != null ? this._browseFeatSortState : DEFAULT_SORT_FIELD;
        break;
      case "myApps":
        sortField = this._myAppsSortState != null ? this._myAppsSortState : DEFAULT_SORT_MYAPPS;
        break;
    }
    this.sortField = sortField;
    this.selectedSortField = _sortTermsToSortFields
      .find(((termToField => termToField[1] === sortField)))[0];
  }

  handleSortSelectionChange(e: any) {
    this.sortField = e?.detail?.requestedDropdownItem?.value;
    this.selectedSortField = e?.detail?.requestedDropdownItem?.textContent;

    switch (this.navigationRoute) {
      case "browseAll":
        this._browseFeatSortState = this.sortField;
        break;
      case "featured":
        this._browseFeatSortState = this.sortField;
        break;
      case "myApps":
        this._myAppsSortState = this.sortField;
        break;
    }
  }

  /** Use the App Type Item's Tags to populate the Search Boxes' suggestions */
  setSearchSelectionWithTags(items: __esri.PortalItem[]) {
    const suggestionSet: Set<string> = new Set();
    items.forEach((item: __esri.PortalItem) => {
      item.tags.forEach(tag => suggestionSet.add(tag));
    });
    this.searchBox.viewModel.searchSuggestionsExclude.forEach((suggestionToExclude: string) => {
      if (suggestionSet.has(suggestionToExclude)) { suggestionSet.delete(suggestionToExclude); }
    })

    const searchSuggestions: Collection<string> = new Collection(Array.from(suggestionSet.keys()));
    searchSuggestions.sort();

    whenDefinedOnce(this, "searchBox", () => {
      this.searchBox.viewModel.setSearchSuggestions(searchSuggestions);
    });
  }

}
